import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button, CircularProgress, Box, Typography } from "@mui/material";
import GoogleIcon from '@mui/icons-material/Google';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { motion, AnimatePresence } from "framer-motion";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";

import { app } from "../../../../firebase";
import { google } from "../../../../redux/auth/authSlice";
import { API_CALL_RES_STATUS } from "../../../../assets/constants/constants";
import { useNotification } from "../../../shared/NotificationProvider";

const OAuth = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotification();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleGoogleClick = async () => {
    setLoading(true);
    setSuccess(false);
    
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const auth = getAuth(app);
      const result = await signInWithPopup(auth, provider);

      const res = await dispatch(
        google({
          name: result.user.displayName,
          email: result.user.email,
          photo: result.user.photoURL,
        })
      );

      if (res.payload?.status === API_CALL_RES_STATUS.SUCCESS) {
        setSuccess(true);
        notify.success('Successfully signed in with Google!');
        
        // Add a small delay for better UX
        setTimeout(() => {
          navigate(location.state?.from?.pathname || "/fmr/posts");
        }, 1500);
        
      } else if (res.payload?.status === API_CALL_RES_STATUS.FAILURE) {
        notify.error(res.payload.message || "Google sign-in failed. Please try again.");
      } else {
        notify.error("Something went wrong. Please try again later.");
      }
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        notify.warning('Google sign-in was cancelled. Please try again.');
      } else if (error.code === 'auth/network-request-failed') {
        notify.error('Network error. Please check your internet connection.');
      } else {
        notify.error("Google authentication failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ width: '100%' }}
    >
      <Button
        onClick={handleGoogleClick}
        variant="outlined"
        type="button"
        disabled={loading || success}
        sx={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          color: 'rgba(255, 255, 255, 0.9)',
          "&:hover": {
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(255, 255, 255, 0.1)',
          },
          "&:active": {
            transform: 'translateY(0)',
          },
          "&:disabled": {
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            color: 'rgba(255, 255, 255, 0.3)',
            cursor: 'not-allowed',
          },
          textTransform: "none",
          fontWeight: 600,
          padding: "14px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          borderRadius: '12px',
          fontSize: '0.95rem',
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
            transform: 'translateX(-100%)',
            transition: 'transform 0.6s ease',
          },
          '&:hover::before': {
            transform: 'translateX(100%)',
          },
        }}
      >
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <CircularProgress 
                size={20} 
                sx={{ 
                  color: '#00ff88',
                }} 
              />
              <Typography sx={{ color: 'inherit', fontWeight: 600 }}>
                Connecting...
              </Typography>
            </motion.div>
          ) : success ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <CheckCircleIcon sx={{ color: '#00ff88', fontSize: '1.4rem' }} />
              <Typography sx={{ color: 'inherit', fontWeight: 600 }}>
                Connected Successfully!
              </Typography>
            </motion.div>
          ) : (
            <motion.div
              key="default"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <GoogleIcon 
                  sx={{ 
                    fontSize: '1.2rem',
                    color: '#DB4437',
                  }} 
                />
              </Box>
              <Typography sx={{ color: 'inherit', fontWeight: 600 }}>
                Continue with Google
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>
      </Button>

      {/* Floating particles on hover */}
      <AnimatePresence>
        {!loading && !success && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          >
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ 
                  opacity: [0, 0.5, 0],
                  scale: [0, 1, 0],
                  x: [0, (i - 1) * 10, 0],
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: i * 0.3,
                  ease: "easeOut"
                }}
                style={{
                  position: 'absolute',
                  fontSize: '8px',
                  color: '#00ff88',
                  left: `${30 + i * 20}%`,
                  top: '50%',
                }}
              >
                ✨
              </motion.div>
            ))}
          </Box>
        )}
      </AnimatePresence>

      {/* Additional Info */}
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(255, 255, 255, 0.5)',
          textAlign: 'center',
          mt: 2,
          display: 'block',
          fontSize: '0.75rem',
          lineHeight: 1.4,
        }}
      >
        By continuing, you agree to our Terms of Service and Privacy Policy.
        We'll never post anything without your permission.
      </Typography>
    </motion.div>
  );
};

export default OAuth;